import { useCallback, useState } from "react";
import { Tags } from "lucide-react";
import { useNavigate } from "react-router-dom";

import ACL_RELATIONSHIPS from "acl-relationships";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { TableCreateButton } from "components/BaseTableV3/TableCreateButton";
import { SELECT_COLUMN_ID } from "constants/Constants";
import { getNextQueryParams } from "components/helpers";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import useAuthorized from "hooks/useAuthorized";
import TagService from "services/Tag";
import {
  useTagManagementTable,
  useTagMutations,
  invalidateTagsQuery,
} from "hooks/useTagManagementTable";
import BulkUpdateCategoryModal from "routes/tags/components/BulkUpdateCategoryModal";
import { TagManagementTableActions } from "routes/tags/components/TagManagementTableActions";

export const TAG_MANAGEMENT_TABLE_CONSTANTS = {
  tableName: "tag_management",
  queryKey: "tags",
  className: "Tag",
  exportTableName: "TagsTable",
  header: "Tag Management",
  headerIcon: Tags,
};

export const TAG_COLUMN_KEYS = {
  SELECT: "select",
  ID: "id",
  NAME: "name",
  DESCRIPTION: "description",
  COLOR: "color",
  TAGGABLE_MODELS: "taggable_models",
  READ_ROLES: "read_roles",
  EDIT_ROLES: "edit_roles",
  ENABLED: "enabled",
  CATEGORY: "tag_category.name",
  CREATED_BY: "created_by_user.full_name",
  CREATED_AT: "created_at",
  UPDATED_BY: "updated_by_user.full_name",
  UPDATED_AT: "updated_at",
  ACTIONS: "actions",
};

export const TAG_COLUMN_IDS = {
  SELECT: SELECT_COLUMN_ID,
  ID: "ID",
  NAME: "Name",
  DESCRIPTION: "Description",
  COLOR: "Color",
  TAGGABLE_MODELS: "Taggable Models",
  READ_ROLES: "Read Roles",
  EDIT_ROLES: "Edit Roles",
  ENABLED: "Enabled",
  CATEGORY: "Category",
  CREATED_BY: "Created By",
  CREATED_AT: "Created At",
  UPDATED_BY: "Updated By",
  UPDATED_AT: "Updated At",
  ACTIONS: "Actions",
};

export const TAG_FILTER_KEYS = {
  TAGGABLE_MODELS: "taggable_models",
  CATEGORY_IDS: "category_ids",
};

export const TAG_FILTER_TITLES = {
  TAGGABLE_MODELS: "Taggable Models",
  CATEGORY: "Category",
};

const TagManagementTableV3 = () => {
  const navigate = useNavigate();
  const canEditTag = useAuthorized([ACL_RELATIONSHIPS.tag.edit]);
  const canDeleteTag = useAuthorized([ACL_RELATIONSHIPS.tag.delete]);

  const [confirm, setConfirm] = useState(null);
  const [bulkCategoryTagIds, setBulkCategoryTagIds] = useState(null);

  const { remove, bulkRemove } = useTagMutations({
    onDeleted: () => setConfirm(null),
  });

  const handleEditTag = useCallback(
    tag => navigate(`/settings/tags/${tag.id}/edit`),
    [navigate]
  );

  const handleCloneTag = useCallback(
    tag => navigate(`/settings/tags/new?clone=${tag.id}`),
    [navigate]
  );

  const handleDeleteTag = useCallback(
    tag =>
      setConfirm({
        title: "Delete tag?",
        description: tag?.name
          ? `Are you sure you want to delete the tag "${tag.name}"?`
          : "Are you sure you want to delete this tag?",
        onConfirm: () => remove.mutate(tag.id),
      }),
    [remove]
  );

  const handleBulkDelete = useCallback(
    tagIds =>
      setConfirm({
        title: "Delete tags?",
        description: `Are you sure you want to delete ${tagIds.length} tag(s)?`,
        onConfirm: () => bulkRemove.mutate(tagIds),
      }),
    [bulkRemove]
  );

  const handleBulkUpdateCategory = useCallback(
    tagIds => setBulkCategoryTagIds(tagIds),
    []
  );

  const { filters, columns } = useTagManagementTable({
    canEditTag,
    canDeleteTag,
    onEditTag: handleEditTag,
    onCloneTag: handleCloneTag,
    onDeleteTag: handleDeleteTag,
  });

  const fetchTags = useCallback(
    queryParams =>
      TagService.getTagsTable(getNextQueryParams(queryParams, columns)),
    [columns]
  );

  const actionButtons = ({ selectedIds }) => (
    <TagManagementTableActions
      selectedIds={selectedIds}
      canEditTag={canEditTag}
      canDeleteTag={canDeleteTag}
      onBulkDelete={handleBulkDelete}
      onBulkUpdateCategory={handleBulkUpdateCategory}
    />
  );

  const createButton = (
    <TableCreateButton
      permissions={[ACL_RELATIONSHIPS.tag.create]}
      to="/settings/tags/new"
    >
      New Tag
    </TableCreateButton>
  );

  return (
    <>
      <ConfirmDeleteDialog
        open={confirm != null}
        onOpenChange={next => !next && setConfirm(null)}
        title={confirm?.title}
        description={confirm?.description}
        onConfirm={confirm?.onConfirm}
      />
      <BulkUpdateCategoryModal
        open={bulkCategoryTagIds != null}
        onOpenChange={next => !next && setBulkCategoryTagIds(null)}
        tagIds={bulkCategoryTagIds ?? []}
        onSuccess={() => {
          setBulkCategoryTagIds(null);
          invalidateTagsQuery();
        }}
      />
      <BaseTableV3
        columns={columns}
        filters={filters}
        headerIcon={TAG_MANAGEMENT_TABLE_CONSTANTS.headerIcon}
        header={TAG_MANAGEMENT_TABLE_CONSTANTS.header}
        queryKey={TAG_MANAGEMENT_TABLE_CONSTANTS.queryKey}
        className={TAG_MANAGEMENT_TABLE_CONSTANTS.className}
        tableName={TAG_MANAGEMENT_TABLE_CONSTANTS.tableName}
        exportTableName={TAG_MANAGEMENT_TABLE_CONSTANTS.exportTableName}
        fetchData={fetchTags}
        createButton={createButton}
        actionButtons={actionButtons}
        invalidateData={invalidateTagsQuery}
        enableSearch
        enableSettings
      />
    </>
  );
};

export default TagManagementTableV3;
